import { useClientId, useCopyPasteStyles, useDeviceType } from "@blocks/hooks";
import { Button, Flex, FlexItem } from "@wordpress/components";
import { useSelect, withSelect } from "@wordpress/data";
import { dateI18n, __experimentalGetSettings } from "@wordpress/date";
import { Fragment, useMemo, useState } from "@wordpress/element";
import { EditProps } from "blocks/types";
import classnames from "classnames";
import React from "react";
import { Icon } from "../../components";
import { useBlockStyle } from "../../hooks";
import "../assets/sass/blocks/tab-post/style.scss";
import InspectorControls from "./components/InspectorControls";
import { __ } from "@wordpress/i18n";
import { cn } from "@blocks/utils";
import { upperFirst } from "lodash";

interface Props extends EditProps<any> {
	categories: Array<any>;
	popularPosts: Array<any>;
	tags: Array<any>;
	numberOfPosts: number;
	page: number;
	latestPosts: Array<any>;
	author: Array<any>;
}

const Edit: React.ComponentType<Props> = (props) => {
	const {
		latestPosts,
		popularPosts,
		className,
		attributes: {
			layout,

			category,
			size,
			hideOnDesktop,

			postTitleMarkup,

			enableCategory,
			enableExcerpt,

			metaPosition,
			enableAuthor,
			enableDate,
			separatorType,
			enableIcon,
		},
		setAttributes,
		categories,
		tags,
		numberOfPosts,
		page,
	} = props;

	const { clientId } = useClientId(props);
	const { deviceType } = useDeviceType();
	const { CopyPasterStyleBlockControl } = useCopyPasteStyles();
	const { Style } = useBlockStyle({
		blockName: "tab-post",
		clientId,
		attributes: props.attributes,
		deviceType,
	});

	const [tab, setTab] = useState("latest");

	const TagName = postTitleMarkup || "h3";

	// @ts-ignore
	const authors = useSelect((select) => {
		const { getUsers } = select("core") as any;
		return getUsers({ per_page: -1 });
	}) as any[];

	const authorOptions = authors
		? authors.map((author) => ({
				value: author?.id.toString(),
				label: author?.name,
		  }))
		: [];

	const postTypeOptions = useSelect((select) => {
		const { getPostTypes } = select("core") as any;
		const { getCurrentPost } = select("core/editor") as any;

		const allPostTypes = getPostTypes({ per_page: -1 }) || [];
		const post = getCurrentPost();
		const meta = post?.meta?._mzb_template;
		return allPostTypes
			.filter(
				(type: any) =>
					type.viewable && type.rest_base && type.rest_namespace
			)
			.filter((type: any) => type.slug !== "mzb-builder-template")
			.map((type: any) => {
				if (type.slug === "mzb-builder" && meta) {
					return {
						value: type.slug,
						label: `${upperFirst(meta)} Builder`,
					};
				}
				return {
					value: type.slug,
					label: type.name || type.slug,
				};
			});
	}, []);

	const currentCategoryIndex = useMemo(() => {
		return categories
			? categories.findIndex((cat) => cat?.id === parseInt(category))
			: -1; // Use -1 or another appropriate default value
	}, [category, categories]);

	const classNames = classnames(
		`mzb-tab-post mzb-tab-post-${clientId}`,
		size && `is-${size}`,
		className,
		hideOnDesktop && "magazine-blocks-hide-on-desktop"
	);

	const NumberedPagination = ({
		totalPages,
		currentPage,
		onPageChange,
	}: {
		totalPages: number;
		currentPage: number;
		onPageChange: (val: number) => void;
	}) => {
		if (totalPages < 2) {
			return null; // Don't render pagination if there's only one page
		}

		const pages = Array.from(
			{ length: totalPages },
			(_, index) => index + 1
		);

		return (
			<Flex>
				{pages.map((page) => (
					<FlexItem key={page}>
						<Button
							isPrimary={page === currentPage}
							onClick={() => onPageChange(page)}
						>
							{page}
						</Button>
					</FlexItem>
				))}
			</Flex>
		);
	};

	// Fetch the next page of posts
	const loadMorePosts = (nextPage: number) => {
		// Update the block's attributes to trigger a re-render with the next page
		setAttributes({ page: nextPage });
	};

	// Render numbered pagination
	const pagination = (
		<NumberedPagination
			totalPages={Math.ceil(numberOfPosts / 4)}
			currentPage={page}
			onPageChange={loadMorePosts}
		/>
	);

	return (
		<Fragment>
			<InspectorControls
				attributes={props.attributes}
				setAttributes={setAttributes}
				categories={categories}
				tags={tags}
				authorOptions={authorOptions}
				postTypeOptions={postTypeOptions}
			/>
			<CopyPasterStyleBlockControl withBlockControls />
			<Style />
			<div className={classNames}>
				{
					<div className="mzb-posts">
						<div
							className={`mzb-tab-controls  ${
								"style1" === layout
									? `mzb-tab-layout-${layout}`
									: ""
							}`}
						>
							<div
								data-value="latest"
								onClick={(e) => {
									setTab(
										(e.target as HTMLElement).getAttribute(
											"data-value"
										) ?? ""
									); // Provide a default value if null
								}}
								className={`mzb-tab-title ${
									"latest" === tab ? "active" : ""
								}`}
							>
								{__("Latest", "magazine-blocks")}
							</div>
							<div
								data-value="popular"
								onClick={(e) => {
									setTab(
										(e.target as HTMLElement).getAttribute(
											"data-value"
										) ?? ""
									); // Provide a default value if null
								}}
								className={`mzb-tab-title ${
									"popular" === tab ? "active" : ""
								}
								`}
							>
								{__("Popular", "magazine-blocks")}
							</div>
						</div>
						{"latest" === tab &&
							(latestPosts || []).map((post, idx) => {
								return (
									<div className="mzb-post" key={idx}>
										{post
											?.magazine_blocks_featured_image_url
											?.thumbnail?.[0] && (
											<div className="mzb-featured-image">
												<img
													src={
														post
															?.magazine_blocks_featured_image_url
															?.thumbnail[0]
													}
												/>
											</div>
										)}
										<div className="mzb-post-content">
											{enableCategory && (
												<div className="mzb-post-meta">
													<span
														className="mzb-post-categories"
														dangerouslySetInnerHTML={{
															__html: post?.magazine_blocks_category,
														}}
													/>
												</div>
											)}

											{metaPosition === "bottom" && (
												<>
													{enableExcerpt && (
														<>
															<TagName className="mzb-post-title">
																<a
																	href={
																		post.link
																	}
																>
																	{
																		post
																			.title
																			.rendered
																	}
																</a>
															</TagName>
														</>
													)}

													<div
														className={cn(
															"mzb-post-entry-meta",
															separatorType &&
																`mzb-meta-separator--${separatorType}`
														)}
													>
														{enableAuthor ===
															true && (
															<span className="mzb-post-author">
																{enableIcon ===
																	true && (
																	<img
																		className="author-display-image"
																		src={
																			post?.magazine_blocks_author_image
																		}
																	/>
																)}
																<a
																	href={
																		post
																			?.magazine_blocks_author
																			?.author_link
																	}
																>
																	{" "}
																	{
																		post
																			?.magazine_blocks_author
																			?.display_name
																	}{" "}
																</a>
															</span>
														)}

														{enableDate ===
															true && (
															<span className="mzb-post-date">
																{enableIcon ===
																	true && (
																	<Icon
																		type="blockIcon"
																		name="calendar"
																		size={
																			24
																		}
																	/>
																)}
																<a href="#">
																	{" "}
																	{dateI18n(
																		__experimentalGetSettings()
																			.formats
																			.date,
																		post?.date_gmt,
																		undefined
																	)}{" "}
																</a>
															</span>
														)}
													</div>
												</>
											)}

											{metaPosition === "top" && (
												<>
													<div
														className={cn(
															"mzb-post-entry-meta",
															separatorType &&
																`mzb-meta-separator--${separatorType}`
														)}
													>
														{enableAuthor ===
															true && (
															<span className="mzb-post-author">
																{enableIcon ===
																	true && (
																	<img
																		className="author-display-image"
																		src={
																			post?.magazine_blocks_author_image
																		}
																	/>
																)}
																<a
																	href={
																		post
																			?.magazine_blocks_author
																			?.author_link
																	}
																>
																	{" "}
																	{
																		post
																			?.magazine_blocks_author
																			?.display_name
																	}{" "}
																</a>
															</span>
														)}

														{enableDate ===
															true && (
															<span className="mzb-post-date">
																{enableIcon ===
																	true && (
																	<Icon
																		type="blockIcon"
																		name="calendar"
																		size={
																			24
																		}
																	/>
																)}
																<a href="#">
																	{" "}
																	{dateI18n(
																		__experimentalGetSettings()
																			.formats
																			.date,
																		post?.date_gmt,
																		undefined
																	)}{" "}
																</a>
															</span>
														)}
													</div>

													{enableExcerpt && (
														<>
															<TagName className="mzb-post-title">
																<a
																	href={
																		post.link
																	}
																>
																	{
																		post
																			.title
																			.rendered
																	}
																</a>
															</TagName>
														</>
													)}
												</>
											)}
										</div>
									</div>
								);
							})}
						{"popular" === tab &&
							(popularPosts || []).map((post, idx) => {
								return (
									<div className="mzb-post" key={idx}>
										{post
											?.magazine_blocks_featured_image_url
											?.thumbnail?.[0] && (
											<div className="mzb-featured-image">
												<img
													src={
														post
															?.magazine_blocks_featured_image_url
															?.thumbnail[0]
													}
												/>
											</div>
										)}
										<div className="mzb-post-content">
											{enableExcerpt && (
												<>
													<h3 className="mzb-post-title">
														<a href={post.link}>
															{
																post.title
																	.rendered
															}
														</a>
													</h3>
												</>
											)}

											<div className="mzb-post-entry-meta">
												<span className="mzb-post-date">
													{enableIcon === true && (
														<Icon
															type="blockIcon"
															name="calendar"
															size={24}
														/>
													)}
													<a href="#">
														{" "}
														{dateI18n(
															__experimentalGetSettings()
																.formats.date,
															post?.date_gmt,
															undefined
														)}{" "}
													</a>
												</span>
											</div>
										</div>
									</div>
								);
							})}
					</div>
				}
			</div>
		</Fragment>
	);
};

// @ts-ignore
export default withSelect((select, props) => {
	const { getEntityRecords } = select("core");
	const {
		attributes: {
			category,
			tag,
			order,
			orderBy,
			orderType,
			authorName,
			excludedCategory,
			page,
			offset,
			postCount,
			postType,
		},
	} = props;
	interface Post {
		magazine_blocks_comment?: number; // Assuming magazine_blocks_comment is a number
	}
	const query = {
		order,
	};

	const selectedPostType = postType || "post";

	return {
		latestPosts: (
			getEntityRecords("postType", "post", {
				per_page: -1,
				categories:
					category && category !== "all"
						? parseInt(category)
						: undefined,
				tags: tag && tag !== "all" ? tag : undefined,
				author: authorName ? authorName : undefined,
				categories_exclude:
					excludedCategory && excludedCategory.length > 0
						? excludedCategory
						: undefined,
				post_type: postType,
				offset,
				order: orderType,
				orderby: orderBy,
			}) || []
		)
			.sort(
				(a: any, b: any) =>
					new Date(b?.date_gmt || b?.date || 0).getTime() -
					new Date(a?.date_gmt || a?.date || 0).getTime()
			)
			.slice(0, postCount),
		popularPosts: (
			getEntityRecords("postType", "post", {
				per_page: -1,
				categories:
					category && category !== "all"
						? parseInt(category)
						: undefined,
				tags: tag && tag !== "all" ? tag : undefined,
				author: authorName ? authorName : undefined,
				categories_exclude:
					excludedCategory && excludedCategory.length > 0
						? excludedCategory
						: undefined,
				post_type: postType,
				offset,
				order: orderType,
				orderby: orderBy,
			}) || []
		)
			.sort(
				(a: Post, b: Post) =>
					(b.magazine_blocks_comment || 0) -
					(a.magazine_blocks_comment || 0)
			)
			.slice(0, postCount),
		numberOfPosts:
			getEntityRecords("postType", selectedPostType, {
				per_page: -1,
				categories:
					"all" === category || selectedPostType !== "post"
						? undefined
						: parseInt(category),
			})?.length || 0,
		categories:
			getEntityRecords("taxonomy", "category", { per_page: -1 }) || [],
		tags: getEntityRecords("taxonomy", "post_tag", { per_page: -1 }) || [],
	};
})(Edit);
